import { EVENT_TYPE, CATEGORY } from './eventTypes.js';
import { SEVERITY } from './severities.js';
import { DUE_BUCKET } from './filterOptions.js';

/**
 * Mock Work Queue dataset mirroring the aims-htl V1 prototype (My Work view).
 *
 * Backend-absent for V1 — the Work Queue list/detail endpoints arrive with
 * CT-4867. Per-event-type stories (CT-4859..CT-4865) enrich the `detail` block
 * for their type; CT-4856 renders the generic detail for everything else.
 *
 * `scope` splits My Work (CT-4857) from My Team (CT-4858).
 */
export const SCOPE = { MY_WORK: 'my_work', MY_TEAM: 'my_team' };

export const MOCK_EVENTS = [
  {
    id: 'EVT-001',
    scope: SCOPE.MY_WORK,
    type: EVENT_TYPE.GOV_PROMOTION,
    category: CATEGORY.GOV,
    severity: SEVERITY.ACT_NOW,
    title: 'DIAN Intake: Financial Policy PDF requires approval',
    description: '7 claims extracted, 2 knowledge conflicts detected. Will block FinancePolicyBot until resolved.',
    refId: 'DIAN-4821',
    refLabel: 'DIAN Intake',
    actionLabel: 'Approve',
    studio: 'governance-operations',
    studioExtraCount: 0,
    status: 'open',
    dueLabel: 'Due now',
    dueBucket: DUE_BUCKET.DUE_TODAY,
    overdue: true,
    isCustomer: false,
    missionCritical: true,
    blocks: { workflows: 14, agents: 3 },
    commentsCount: 2,
    situation: '7 claims extracted, 2 knowledge conflicts detected. Will block FinancePolicyBot until resolved.',
    pipeline: ['Indexing', 'Claim Detection', 'Conflict Check', 'KCON Routing'],
    destination: 'Truth Plane',
    detail: {
      claims: [
        { id: 'CLM-001', confidence: 0.94, text: 'Minimum required return on equity for wealth management accounts is 7.2% per annum' },
        { id: 'CLM-002', confidence: 0.91, text: 'Portfolio rebalancing must occur within 5 business days of threshold breach' },
        { id: 'CLM-003', confidence: 0.88, text: 'Advisory fee cap for managed accounts is 1.5% of AUM annually' },
        { id: 'CLM-004', confidence: 0.95, text: 'Compliance review required for accounts exceeding $2M in exposure' },
        {
          id: 'CLM-005',
          confidence: 0.79,
          conflict: true,
          text: 'Interest rate sensitivity threshold: ±150bps triggers mandatory review',
          sourceA: { label: 'Financial Policy Manual v12', value: '±150bps' },
          sourceB: { label: 'Risk Framework Q1 2026', value: '±200bps' },
        },
        { id: 'CLM-006', confidence: 0.92, text: 'Client notification required within 48 hours of material portfolio change' },
        {
          id: 'CLM-007',
          confidence: 0.71,
          conflict: true,
          text: 'Liquidity reserve minimum is 8% of total portfolio value',
          sourceA: { label: 'Financial Policy Manual v12', value: '8% reserve' },
          sourceB: { label: 'Basel III Compliance Template', value: '10% reserve' },
        },
      ],
    },
  },
  {
    id: 'EVT-002',
    scope: SCOPE.MY_WORK,
    type: EVENT_TYPE.HTL_CONTINUATION,
    category: CATEGORY.AGNT,
    severity: SEVERITY.CRITICAL,
    title: 'HITL Pause: SalesForecastPA about to send external email',
    description: 'Agent paused before GE-COMM action. Confidence 0.71 — below threshold. External recipient: partner@gefinancial.com.',
    refLabel: 'HTL Continuation',
    actionLabel: 'Approve',
    studio: 'agentic-oversight',
    studioExtraCount: 0,
    status: 'paused',
    dueLabel: 'Paused',
    dueBucket: DUE_BUCKET.DUE_TODAY,
    overdue: false,
    isCustomer: false,
    missionCritical: true,
    blocks: { workflows: 3, agents: 1 },
    commentsCount: 2,
    situation: 'Agent paused before GE-COMM action. Confidence 0.71 — below threshold. External recipient: partner@gefinancial.com.',
    pipeline: ['Intent', 'Draft', 'Policy Check', 'Send'],
    destination: 'Sandbox Plane',
    detail: {
      workflow: 'SalesForecastPA External Comms',
      agent: 'SalesForecastPA',
      model: 'GE-Comms-v2.1',
      confidence: 0.71,
      geClassification: 'GE-COMM',
      traceRef: 'WF-7712',
      output: {
        channel: 'Email',
        recipient: 'partner@gefinancial.com',
        subject: 'Q3 2026 Sales Forecast — Northfield Partners Portfolio Review',
        draft:
          'Dear Partner,\n\nWe are pleased to share the Q3 2026 sales forecast for your portfolio. Based on our analysis of current market conditions and historical performance, we project a 12.4% increase in AUM over the next quarter.\n\nKey highlights:\n• Revenue trajectory: $4.2M → $4.72M projected\n• Risk-adjusted return: 8.1% (above 7.2% target)\n• Top performing segment: Wealth Management (+18.3%)\n\nPlease review the attached detailed report.\n\nBest regards,\nSalesForecastPA on behalf of Northfield Partners Advisory',
      },
      trace: [
        {
          label: 'Forecast Generated',
          description: 'Q3 sales forecast compiled from CRM + pipeline data',
          timestamp: '04:10 AM',
          state: 'done',
        },
        {
          label: 'Recipient Lookup',
          description: 'Recipient resolved: partner@gefinancial.com (external)',
          timestamp: '04:11 AM',
          state: 'done',
        },
        {
          label: 'Confidence Check',
          description: 'Confidence 0.71 — below 0.85 threshold for auto-send',
          timestamp: '04:11 AM',
          state: 'done',
        },
        {
          label: 'HITL Gate',
          description: 'Waiting for human authorization to send external email',
          timestamp: '04:11 AM',
          state: 'paused',
        },
        {
          label: 'Send / Cancel',
          description: 'Action pending human decision',
          state: 'pending',
        },
      ],
    },
  },
  {
    id: 'EVT-004',
    scope: SCOPE.MY_WORK,
    type: EVENT_TYPE.GOV_BREAK_GLASS,
    category: CATEGORY.GOV,
    severity: SEVERITY.ACT_NOW,
    title: 'Break Glass: Temp PII partition access request',
    description: 'Emergency PII access for incident response. Requires 2 approvers. 1 of 2 approvals received.',
    refId: 'BG-0091',
    refLabel: 'Break Glass',
    actionLabel: 'Approve',
    studio: 'governance-operations',
    studioExtraCount: 0,
    status: 'awaiting_second_approval',
    dueLabel: 'Awaiting 2nd approval',
    dueBucket: DUE_BUCKET.DUE_TODAY,
    overdue: false,
    isCustomer: false,
    missionCritical: true,
    blocks: { workflows: 1, agents: 0 },
    commentsCount: 2,
    situation: 'Emergency PII access for incident response. Requires 2 approvers. 1 of 2 approvals received.',
    pipeline: ['Request', 'Approval 1', 'Approval 2', 'Grant'],
    destination: 'Truth Plane',
    detail: {
      requester: { name: 'Marcus H.', role: 'Governance Lead', initials: 'MH' },
      requestedAt: 'Jun 17, 03:00 AM',
      incidentRef: 'IR-2026-0617',
      partition: 'PII — Identity Records',
      classification: 'Restricted — PII',
      accessScope: 'Read + Write',
      duration: '4 hours',
      reason:
        'Incident IR-2026-0617: Unauthorized access detected in identity partition. Requires immediate forensic review to assess scope and remediate.',
      security: {
        lastBreakGlass: '2026-05-02 by Devon N. — Approved — no incidents.',
        riskTier: 'Critical',
      },
      requiredCount: 2,
      approvers: [
        {
          id: 'devon-n',
          name: 'Devon N.',
          role: 'IT Security',
          initials: 'DN',
          status: 'confirmed',
          confirmedAt: 'Jun 17, 03:10 AM',
        },
        { id: 'alexa-m', name: 'Alexa M.', role: 'Admin', initials: 'AM', status: 'pending' },
      ],
    },
  },
  {
    id: 'EVT-C002',
    scope: SCOPE.MY_WORK,
    type: EVENT_TYPE.CLIENT_CONTINUATION,
    category: CATEGORY.CLIENT,
    severity: SEVERITY.CRITICAL,
    title: 'Website Chat: customer expressing cancellation intent',
    description: 'CustomerEscalationPA paused — GE-COMM action blocked. Customer used explicit cancellation language. Confidence 0.71. Pack rule: cancellation intent requires human.',
    refLabel: 'Client · Continuation',
    actionLabel: 'Approve',
    studio: 'governance-operations',
    studioExtraCount: 1,
    status: 'live',
    dueLabel: 'Live — customer waiting',
    dueBucket: DUE_BUCKET.DUE_TODAY,
    overdue: true,
    isCustomer: true,
    missionCritical: true,
    blocks: { workflows: 2, agents: 1 },
    commentsCount: 2,
    situation: 'CustomerEscalationPA paused — GE-COMM action blocked. Customer used explicit cancellation language. Confidence 0.71. Pack rule: cancellation intent requires human.',
    pipeline: ['Detect', 'Classify', 'Pause', 'Human'],
    destination: 'Sandbox Plane',
    detail: {
      workflow: 'Customer Webchat Escalation',
      agent: 'CustomerEscalationPA',
      model: 'GE-Comms-v2.1',
      confidence: 0.71,
      geClassification: 'GE-COMM',
      ucpProfileUrl: 'UCP-ACC-8841',
      traceRef: 'WF-8210',
      output: {
        channel: 'Webchat',
        recipient: 'Jordan Hale (live)',
        subject: 'Retention — cancellation intent',
        draft:
          'Hi Jordan — I hear you want to cancel. Before we close the account I can offer a pause or a plan change. Should I proceed with cancellation, or would you like to talk to a specialist first?',
      },
      trace: [
        {
          label: 'Cancellation Language Detected',
          description: 'Customer wrote "cancel my account" in the live webchat',
          timestamp: '09:02 AM',
          state: 'done',
        },
        {
          label: 'Intent Classified',
          description: 'Classified as GE-COMM — retention conversation',
          timestamp: '09:02 AM',
          state: 'done',
        },
        {
          label: 'Pack Rule Applied',
          description: 'Cancellation intent requires a human before any reply',
          timestamp: '09:03 AM',
          state: 'done',
        },
        {
          label: 'HITL Gate',
          description: 'Customer is waiting in the chat for a response',
          timestamp: '09:03 AM',
          state: 'paused',
        },
        {
          label: 'Send / Cancel',
          description: 'Action pending human decision',
          state: 'pending',
        },
      ],
    },
  },
  {
    id: 'EVT-C004',
    scope: SCOPE.MY_WORK,
    type: EVENT_TYPE.CLIENT_HANDOFF,
    category: CATEGORY.CLIENT,
    severity: SEVERITY.ACT_NOW,
    title: 'Voice: vulnerable customer signal — immediate human required',
    description: 'VoiceAssistantPA detected vulnerable customer indicators during call. Council classified as GE-COMP. Transferred to human immediately per policy.',
    refLabel: 'Client · Handoff',
    actionLabel: 'Take it',
    studio: 'governance-operations',
    studioExtraCount: 1,
    status: 'live',
    dueLabel: 'Immediate — call in progress',
    dueBucket: DUE_BUCKET.DUE_TODAY,
    overdue: true,
    isCustomer: true,
    missionCritical: true,
    blocks: { workflows: 1, agents: 1 },
    commentsCount: 2,
    situation: 'VoiceAssistantPA detected vulnerable customer indicators during call. Council classified as GE-COMP. Transferred to human immediately per policy.',
    pipeline: ['Listen', 'Classify', 'Transfer'],
    destination: 'Sandbox Plane',
  },
  {
    id: 'EVT-006',
    scope: SCOPE.MY_WORK,
    type: EVENT_TYPE.GOV_REVIEW,
    category: CATEGORY.GOV,
    severity: SEVERITY.CRITICAL,
    title: 'Review requested: Interest rate sensitivity threshold — need your call',
    description: 'Marcus H. flagged this claim as a conflict outside his sign-off authority and is asking you to make the call before the DIAN proposal can be attested.',
    refId: 'KBU-4490',
    refLabel: 'Review Request',
    actionLabel: 'Review',
    studio: 'governance-operations',
    studioExtraCount: 0,
    status: 'open',
    dueLabel: 'Jun 23',
    dueBucket: DUE_BUCKET.THIS_WEEK,
    overdue: false,
    isCustomer: false,
    missionCritical: false,
    blocks: { workflows: 14, agents: 3 },
    commentsCount: 2,
    situation: 'Marcus H. flagged this claim as a conflict outside his sign-off authority and is asking you to make the call before the DIAN proposal can be attested.',
    pipeline: ['Flag', 'Review', 'Attest'],
    destination: 'Truth Plane',
    detail: {
      requestedBy: { name: 'Marcus H.', role: 'Governance Lead', initials: 'MH' },
      requestedAt: 'Jun 17, 04:40 AM',
      requestReason:
        "I don't have sign-off authority on interest rate sensitivity thresholds — this conflicts with the Risk Framework Q1 2026 update and needs Compliance Lead review before I can attest to it.",
      linkedProposal: {
        id: 'EVT-001',
        spec: 'DIAN-4821',
        title: 'DIAN Intake: Financial Policy PDF requires approval',
      },
      claims: [
        {
          id: 'CLM-005',
          confidence: 0.79,
          conflict: true,
          text: 'Interest rate sensitivity threshold: ±150bps triggers mandatory review',
          sourceA: { label: 'Financial Policy Manual v12', value: '±150bps' },
          sourceB: { label: 'Risk Framework Q1 2026', value: '±200bps' },
        },
      ],
    },
  },
  {
    id: 'EVT-011',
    scope: SCOPE.MY_WORK,
    type: EVENT_TYPE.TRAIN_ME,
    category: CATEGORY.GOV,
    severity: SEVERITY.ACTION,
    delegatedFrom: 'Priya K.',
    title: 'Train Me: Regional multiplier correction awaiting decision',
    description: 'Model predicted 1.12 but ground truth is 1.15. Your decision will update the regional pricing model.',
    refLabel: 'Train Me',
    actionLabel: 'Train Me',
    studio: 'governance-operations',
    studioExtraCount: 0,
    status: 'open',
    dueLabel: 'Open',
    dueBucket: DUE_BUCKET.NO_DATE,
    overdue: false,
    isCustomer: false,
    missionCritical: false,
    blocks: { workflows: 0, agents: 0 },
    commentsCount: 2,
    situation: 'Model predicted 1.12 but ground truth is 1.15. Your decision will update the regional pricing model.',
    detail: {
      submitter: { name: 'Jordan T.', role: 'Pricing Analyst', initials: 'JT' },
      submittedAt: 'Jun 14, 2026',
      note: 'Ground-truth reconciliation from the Q2 regional audit. The model multiplier is stale.',
      fieldName: 'Regional multiplier (LATAM)',
      currentValue: '1.12',
      proposedValue: '1.15',
      sourceRecord: 'PRICE-LATAM-004',
      partition: 'pricing/regional',
      version: 'v12',
      impact: {
        agents: ['PricingBot', 'ForecastPA'],
        workflows: ['Regional Quote Generation', 'Margin Recalculation'],
      },
    },
    pipeline: ['Detect', 'Compare', 'Correct', 'Promote'],
    destination: 'Truth Plane',
  },
  {
    id: 'EVT-012',
    scope: SCOPE.MY_WORK,
    type: EVENT_TYPE.GOV_REVIEW,
    category: CATEGORY.GOV,
    severity: SEVERITY.ACTION,
    title: 'Conflict in Truth: GE-COMP threshold — two sources disagree',
    description: 'Compliance threshold GE-COMP-004: Source A says 0.85, Source B says 0.90. Resolution determines bot behavior.',
    refId: 'CONF-GE-004',
    refLabel: 'Review Request',
    actionLabel: 'Resolve',
    studio: 'governance-operations',
    studioExtraCount: 0,
    status: 'open',
    dueLabel: 'Open',
    dueBucket: DUE_BUCKET.NO_DATE,
    overdue: false,
    isCustomer: false,
    missionCritical: false,
    blocks: { workflows: 6, agents: 3 },
    commentsCount: 2,
    situation: 'Compliance threshold GE-COMP-004: Source A says 0.85, Source B says 0.90. Resolution determines bot behavior.',
    pipeline: ['Detect', 'Compare', 'Resolve'],
    destination: 'Truth Plane',
    detail: {
      requestedBy: { name: 'Devon N.', role: 'IT Security', initials: 'DN' },
      requestedAt: 'Jun 17, 2026',
      requestReason:
        'Compliance Standards Manual v4 supersedes Risk Framework Q1 2026 following the May compliance update. The 0.90 threshold reflects the current regulatory requirement.',
      linkedProposal: {
        id: 'EVT-001',
        spec: 'GE-COMP-004',
        title: 'GE-COMP threshold conflict awaiting resolution',
      },
      claims: [
        {
          id: 'CLM-GE-004',
          confidence: 0.78,
          conflict: true,
          text: 'Compliance threshold GE-COMP-004 is 0.85',
          sourceA: { label: 'Risk Framework Q1 2026', value: '0.85' },
          sourceB: { label: 'Compliance Standards Manual v4', value: '0.90' },
        },
      ],
    },
  },
  {
    id: 'EVT-021',
    scope: SCOPE.MY_WORK,
    type: EVENT_TYPE.HTL_HANDOFF,
    category: CATEGORY.AGNT,
    severity: SEVERITY.ACTION,
    title: 'Lead Qualification: high-intent prospect requires personal follow-up',
    description: 'Agent completed qualification scoring. Lead score 94/100. Handoff triggered — human must handle next contact directly.',
    refId: 'LEAD-8834',
    refLabel: 'HTL Handoff',
    actionLabel: 'Acknowledge',
    studio: 'agentic-oversight',
    studioExtraCount: 0,
    status: 'awaiting_follow_up',
    dueLabel: 'Awaiting follow-up',
    dueBucket: DUE_BUCKET.THIS_WEEK,
    overdue: false,
    isCustomer: false,
    missionCritical: true,
    blocks: { workflows: 1, agents: 1 },
    commentsCount: 2,
    situation: 'Agent completed qualification scoring. Lead score 94/100. Handoff triggered — human must handle next contact directly.',
    pipeline: ['Score', 'Handoff', 'Follow-up'],
    destination: 'Sandbox Plane',
    detail: {
      workflow: 'AdvisorCopilot Lead Qualification',
      entity: 'Jordan Ellis — Northfield Capital Partners',
      recordId: 'LEAD-8834',
      source: 'Web Inquiry Form',
      handoffJustification:
        'Lead score 94/100 exceeds the 90-point auto-handoff threshold',
      traceRef: 'WF-4410',
      packet: {
        keyFacts: [
          '$12M portfolio under management',
          'Requested callback within 24 hours',
          'Expressed dissatisfaction with current advisors',
        ],
        recommendations: [
          'Lead with responsiveness — prospect cited slow response times from current advisor',
          'Reference competitive fee structures for portfolios over $10M',
        ],
        crmRecord: 'CRM-99213',
        knowledgeContract: 'KC-ADVISORY-ONBOARDING',
      },
      agentSummary:
        'Prospect asked about portfolio rebalancing fees and advisor availability; agent answered fee questions and offered to schedule a call with a human advisor.',
      trace: [
        {
          label: 'Lead Captured',
          description: 'Inbound inquiry captured from the web form',
          timestamp: '02:00 AM',
          state: 'done',
        },
        {
          label: 'Qualification Scoring',
          description: 'Agent scored the lead 94/100',
          timestamp: '02:05 AM',
          state: 'done',
        },
        {
          label: 'Threshold Check',
          description: 'Score exceeds the 90-point auto-handoff threshold',
          timestamp: '02:06 AM',
          state: 'done',
        },
        {
          label: 'HTL Handoff',
          description: 'Ownership transferred — agent will not continue',
          timestamp: '02:06 AM',
          state: 'paused',
        },
      ],
    },
  },
  {
    id: 'EVT-022',
    scope: SCOPE.MY_WORK,
    type: EVENT_TYPE.QUESTION,
    category: CATEGORY.GOV,
    severity: SEVERITY.ACTION,
    title: 'Q2 governance posture — platform-wide update',
    description: "Platform-wide: Governance is in Watch. Let's close the KCON and promotion-rate dip this week.",
    refId: 'MSG-0601',
    refLabel: 'Broadcast Question',
    actionLabel: 'Reply',
    studio: 'governance-operations',
    studioExtraCount: 0,
    status: 'awaiting_reply',
    dueLabel: 'Awaiting reply',
    dueBucket: DUE_BUCKET.THIS_WEEK,
    overdue: false,
    isCustomer: false,
    missionCritical: false,
    blocks: { workflows: 0, agents: 0 },
    commentsCount: 1,
    situation: "Platform-wide: Governance is in Watch. Let's close the KCON and promotion-rate dip this week.",
    pipeline: ['Ask', 'Reply'],
    detail: {
      questionVariant: 'broadcast',
      messageBody:
        "Platform-wide: Governance is in Watch. Let's close the KCON and promotion-rate dip this week.",
      askedAt: 'Jul 2, 02:30 AM',
      askedBy: { name: 'Quinn S.', role: 'CTO', initials: 'QS', id: 'quinn-s' },
      question:
        'What is blocking us from closing the KCON backlog and reversing the promotion-rate dip this week?',
      context:
        'Governance posture moved to Watch after last week’s review. Leadership wants a concrete plan before Friday.',
      linkedEvent: { id: 'EVT-001', title: 'DIAN Intake: Financial Policy PDF requires approval' },
    },
    destination: 'Truth Plane',
  },
  {
    id: 'EVT-015',
    scope: SCOPE.MY_WORK,
    type: EVENT_TYPE.GOV_REVIEW,
    category: CATEGORY.GOV,
    severity: SEVERITY.HEADS_UP,
    title: 'Quarterly access recertification due',
    description: 'Q2 access recertification window opens Jun 20. 24 users require recertification across 3 partitions.',
    refId: 'RECERT-Q2-2026',
    refLabel: 'Access Review',
    actionLabel: 'Review',
    studio: 'governance-operations',
    studioExtraCount: 0,
    status: 'open',
    dueLabel: 'Jun 20',
    dueBucket: DUE_BUCKET.THIS_WEEK,
    overdue: false,
    isCustomer: false,
    missionCritical: false,
    blocks: { workflows: 0, agents: 0 },
    commentsCount: 2,
    situation: 'Q2 access recertification window opens Jun 20. 24 users require recertification across 3 partitions.',
    pipeline: ['Notify', 'Recertify', 'Close'],
    destination: 'Truth Plane',
    detail: {
      requestedBy: { name: 'Alexa M.', role: 'Admin', initials: 'AM' },
      requestedAt: 'Jun 18, 09:00 AM',
      requestReason:
        'Q2 access recertification window is open. Please certify or reject each access grant before the cycle closes.',
      claims: [
        {
          id: 'GRN-001',
          confidence: 0.9,
          text: 'Jordan T. — KCON Read — compliance partition (last reviewed 2026-03-15)',
        },
        {
          id: 'GRN-002',
          confidence: 0.88,
          text: 'Riley P. — GE-FIN Read — finance partition (last reviewed 2026-03-15)',
        },
        {
          id: 'GRN-003',
          confidence: 0.85,
          conflict: true,
          text: 'Sam R. — Data Pipeline Write — data-ops (last reviewed 2025-12-01)',
          sourceA: { label: 'Current grant', value: 'Write — data-ops' },
          sourceB: { label: 'Policy recommendation', value: 'Read-only — data-ops' },
        },
      ],
    },
  },

  // ---- My Team (CT-4858) ----
  {
    id: 'EVT-201',
    scope: SCOPE.MY_TEAM,
    type: EVENT_TYPE.GOV_PROMOTION,
    category: CATEGORY.GOV,
    severity: SEVERITY.ACT_NOW,
    title: 'KCON Intake: Q2 pricing addendum requires approval',
    description: '5 claims extracted, 1 conflict detected. Blocking PricingBot until resolved.',
    refId: 'KCON-3390',
    refLabel: 'KCON Intake',
    actionLabel: 'Approve',
    studio: 'governance-operations',
    studioExtraCount: 0,
    status: 'open',
    dueLabel: 'Due now',
    dueBucket: DUE_BUCKET.DUE_TODAY,
    overdue: true,
    isCustomer: false,
    missionCritical: true,
    blocks: { workflows: 6, agents: 2 },
    commentsCount: 1,
    owner: { id: 'jordan-t', name: 'Jordan T.', initials: 'JT', isMine: false },
    situation: '5 claims extracted, 1 conflict detected. Blocking PricingBot until resolved.',
    pipeline: ['Indexing', 'Claim Detection', 'Conflict Check', 'KCON Routing'],
    destination: 'Truth Plane',
    detail: {
      claims: [
        { id: 'CLM-201', confidence: 0.93, text: 'List price freeze applies through 30 September 2026' },
        { id: 'CLM-202', confidence: 0.88, text: 'Volume discount tiers start at 250 seats' },
        { id: 'CLM-203', confidence: 0.9, text: 'Partner margin remains 18% for certified resellers' },
        { id: 'CLM-204', confidence: 0.86, text: 'Promotional SKUs expire at the end of the quarter' },
        {
          id: 'CLM-205',
          confidence: 0.74,
          conflict: true,
          text: 'Enterprise floor price is $42 per seat per month',
          sourceA: { label: 'Q2 Pricing Addendum', value: '$42 / seat' },
          sourceB: { label: 'FY26 Rate Card', value: '$48 / seat' },
        },
      ],
    },
  },
  {
    id: 'EVT-202',
    scope: SCOPE.MY_TEAM,
    type: EVENT_TYPE.HTL_CONTINUATION,
    category: CATEGORY.AGNT,
    severity: SEVERITY.CRITICAL,
    title: 'HITL Pause: RenewalsPA about to apply a non-standard discount',
    description: 'Agent paused before GE-COMM action. Discount 22% exceeds the 15% policy ceiling.',
    refLabel: 'HTL Continuation',
    actionLabel: 'Approve',
    studio: 'agentic-oversight',
    studioExtraCount: 0,
    status: 'paused',
    dueLabel: 'Paused',
    dueBucket: DUE_BUCKET.DUE_TODAY,
    overdue: false,
    isCustomer: false,
    missionCritical: true,
    blocks: { workflows: 2, agents: 1 },
    commentsCount: 0,
    owner: { id: 'sam-r', name: 'Sam R.', initials: 'SR', isMine: false },
    situation: 'Agent paused before GE-COMM action. Discount 22% exceeds the 15% policy ceiling.',
    pipeline: ['Intent', 'Draft', 'Policy Check', 'Send'],
    destination: 'Sandbox Plane',
    detail: {
      workflow: 'Renewals Outreach',
      agent: 'RenewalsPA',
      model: 'GE-Comms-v2.1',
      confidence: 0.58,
      geClassification: 'GE-COMM',
      traceRef: 'WF-7740',
      output: {
        channel: 'Email',
        recipient: 'ops@acmecorp.com',
        subject: 'Your renewal — a special offer inside',
        draft:
          'Hi there,\n\nTo thank you for your continued partnership, we would like to offer a 22% discount on your upcoming renewal. Let us know if you would like to proceed.\n\nBest,\nRenewalsPA',
      },
      trace: [
        {
          label: 'Intent Classified',
          description: 'Renewal outreach with a discount offer',
          timestamp: '11:20 AM',
          state: 'done',
        },
        {
          label: 'Draft Generated',
          description: 'Offer drafted from the renewals template',
          timestamp: '11:21 AM',
          state: 'done',
        },
        {
          label: 'Policy Check',
          description: 'Discount ceiling exceeded — 22% against a 15% cap',
          timestamp: '11:21 AM',
          state: 'done',
        },
        {
          label: 'HITL Gate',
          description: 'Waiting for approval to send the discounted offer',
          timestamp: '11:22 AM',
          state: 'paused',
        },
        {
          label: 'Send / Cancel',
          description: 'Action pending human decision',
          state: 'pending',
        },
      ],
    },
  },
  {
    id: 'EVT-203',
    scope: SCOPE.MY_TEAM,
    type: EVENT_TYPE.GOV_REVIEW,
    category: CATEGORY.GOV,
    severity: SEVERITY.ACTION,
    title: 'Review requested: vendor risk tier reclassification',
    description: 'VendorRiskPA proposes moving Acme Corp from Tier 3 to Tier 2. Needs governance sign-off.',
    refId: 'VND-ACME-T2',
    refLabel: 'Review Request',
    actionLabel: 'Review',
    studio: 'governance-operations',
    studioExtraCount: 0,
    status: 'open',
    dueLabel: 'Jun 24',
    dueBucket: DUE_BUCKET.THIS_WEEK,
    overdue: false,
    isCustomer: false,
    missionCritical: false,
    blocks: { workflows: 2, agents: 2 },
    commentsCount: 3,
    owner: { id: 'marcus-h', name: 'Marcus H.', initials: 'MH', isMine: true },
    situation: 'VendorRiskPA proposes moving Acme Corp from Tier 3 to Tier 2. Needs governance sign-off.',
    pipeline: ['Assess', 'Review', 'Attest'],
    destination: 'Truth Plane',
    detail: {
      requestedBy: { name: 'Marcus H.', role: 'Governance Lead', initials: 'MH' },
      requestedAt: 'Jun 20, 02:15 PM',
      requestReason:
        'VendorRiskPA proposes lowering Acme Corp from Tier 3 to Tier 2 after the latest financial stability assessment. I need a second opinion before attesting.',
      linkedProposal: {
        id: 'EVT-201',
        spec: 'KCON-3390',
        title: 'KCON Intake: Q2 pricing addendum requires approval',
      },
      claims: [
        {
          id: 'CLM-VND-01',
          confidence: 0.91,
          text: 'Acme Corp financial stability score qualifies for Tier 2 vendor risk',
        },
        {
          id: 'CLM-VND-02',
          confidence: 0.74,
          conflict: true,
          text: 'Acme Corp compliance evidence is current through 2026',
          sourceA: { label: 'VendorRiskPA assessment', value: 'Evidence current through 2026' },
          sourceB: { label: 'ProcurementGuard', value: 'Evidence expires Mar 2026' },
        },
        {
          id: 'CLM-VND-03',
          confidence: 0.88,
          text: 'Quarterly Vendor Review workflow can consume the reclassified tier',
        },
      ],
    },
  },
  {
    id: 'EVT-204',
    scope: SCOPE.MY_TEAM,
    type: EVENT_TYPE.HTL_HANDOFF,
    category: CATEGORY.AGNT,
    severity: SEVERITY.HEADS_UP,
    title: 'Lead Qualification: enterprise prospect needs an owner',
    description: 'Agent scored the lead 91/100 and triggered a handoff. No team member has picked it up yet.',
    refLabel: 'HTL Handoff',
    actionLabel: 'Acknowledge',
    studio: 'agentic-oversight',
    studioExtraCount: 0,
    status: 'awaiting_follow_up',
    dueLabel: 'Awaiting follow-up',
    dueBucket: DUE_BUCKET.THIS_WEEK,
    overdue: false,
    isCustomer: false,
    missionCritical: false,
    blocks: { workflows: 1, agents: 1 },
    commentsCount: 0,
    owner: null,
    situation: 'Agent scored the lead 91/100 and triggered a handoff. No team member has picked it up yet.',
    pipeline: ['Score', 'Handoff', 'Follow-up'],
    destination: 'Sandbox Plane',
    detail: {
      workflow: 'Inbound Lead Qualification',
      lastAction: 'Scored lead 94/100 and flagged for personal follow-up',
      handoffReason: 'High-intent prospect requires a human first contact per pack policy.',
      entityContext: 'Northwind Traders — Enterprise, $180k ARR potential',
      packet: {
        findings: [
          'Budget confirmed for Q3',
          'Decision maker identified (VP Ops)',
          'Competing vendor evaluation in progress',
        ],
        recommendations: [
          'Call within 24h',
          'Lead with the integration story',
          'Offer a tailored pilot',
        ],
        nextAction: 'Schedule a discovery call with the VP of Operations',
        linkedRecords: ['LEAD-8821', 'ACC-3390', 'OPP-1247'],
      },
      trace: [
        { label: 'Lead scored', state: 'done' },
        { label: 'Handoff triggered', state: 'done' },
        { label: 'Ownership transfer', state: 'paused' },
      ],
    },
  },
  {
    id: 'EVT-205',
    scope: SCOPE.MY_TEAM,
    type: EVENT_TYPE.GOV_BREAK_GLASS,
    category: CATEGORY.GOV,
    severity: SEVERITY.ACT_NOW,
    title: 'Break Glass: production credential used outside the change window',
    description:
      'PaymentsPA used the emergency credential at 02:14. Requires 2 approvers — nobody on the team has claimed the review yet.',
    refId: 'GOV-8812',
    refLabel: 'Break Glass',
    actionLabel: 'Approve',
    studio: 'governance-operations',
    studioExtraCount: 0,
    status: 'open',
    dueLabel: 'Overdue 4h',
    dueBucket: DUE_BUCKET.DUE_TODAY,
    overdue: true,
    isCustomer: false,
    missionCritical: true,
    blocks: { workflows: 3, agents: 1 },
    commentsCount: 2,
    owner: null,
    situation:
      'Emergency credential used outside the approved change window. Requires two-key Break Glass before the session can continue.',
    pipeline: ['Request', 'Approval 1', 'Approval 2', 'Grant'],
    destination: 'Truth Plane',
    detail: {
      requester: { name: 'PaymentsPA', role: 'Agent (system)', initials: 'PA' },
      requestedAt: 'Jun 20, 02:14 AM',
      incidentRef: 'IR-2026-0620',
      partition: 'payments/prod-credentials',
      classification: 'Restricted — Secrets',
      accessScope: 'Emergency credential use (read + rotate)',
      duration: '1 hour',
      reason:
        'Incident IR-2026-0620: PaymentsPA invoked the break-glass production credential outside the approved change window (02:14 UTC). Need two-key sign-off to attest the session and decide whether to revoke or extend.',
      security: {
        lastBreakGlass: '2026-04-11 by Morgan C. — Approved — change window CHG-4412.',
        riskTier: 'Critical',
      },
      requiredCount: 2,
      approvers: [
        { id: 'morgan-c', name: 'Morgan C.', role: 'Security Lead', initials: 'MC', status: 'pending' },
        { id: 'quinn-s', name: 'Quinn S.', role: 'Executive', initials: 'QS', status: 'pending' },
      ],
    },
  },
  {
    id: 'EVT-206',
    scope: SCOPE.MY_TEAM,
    type: EVENT_TYPE.QUESTION,
    category: CATEGORY.GOV,
    severity: SEVERITY.ACTION,
    title: 'Question: which retention policy applies to archived KCON claims?',
    description: 'Priya K. is blocked on the answer before closing the Q2 retention review.',
    refLabel: 'Question',
    actionLabel: 'Answer',
    studio: 'governance-operations',
    studioExtraCount: 0,
    status: 'open',
    dueLabel: 'Jun 25',
    dueBucket: DUE_BUCKET.THIS_WEEK,
    overdue: false,
    isCustomer: false,
    missionCritical: false,
    blocks: { workflows: 1, agents: 0 },
    commentsCount: 4,
    owner: { id: 'priya-k', name: 'Priya K.', initials: 'PK', isMine: false },
    situation: 'Retention rules differ between the KCON policy and the client contract. Needs a governance ruling.',
    pipeline: ['Ask', 'Answer', 'Close'],
    destination: 'Truth Plane',
  },
  {
    id: 'EVT-207',
    scope: SCOPE.MY_TEAM,
    type: EVENT_TYPE.CLIENT_HANDOFF,
    category: CATEGORY.CLIENT,
    severity: SEVERITY.HEADS_UP,
    title: 'Client handoff: Northwind renewal moving to the success team',
    description: 'Morgan C. owns the transition. Waiting on the account summary before the handoff call.',
    refId: 'ACC-4471',
    refLabel: 'Client Handoff',
    actionLabel: 'Acknowledge',
    studio: 'governance-operations',
    studioExtraCount: 1,
    status: 'awaiting_follow_up',
    dueLabel: 'No date',
    dueBucket: DUE_BUCKET.NO_DATE,
    overdue: false,
    isCustomer: true,
    missionCritical: false,
    blocks: { workflows: 0, agents: 0 },
    commentsCount: 1,
    owner: { id: 'morgan-c', name: 'Morgan C.', initials: 'MC', isMine: false },
    situation: 'Renewal transitions to the success team once the account summary is attached.',
    pipeline: ['Summarize', 'Handoff', 'Confirm'],
    destination: 'Client Plane',
  },
  {
    id: 'EVT-208',
    scope: SCOPE.MY_TEAM,
    type: EVENT_TYPE.TRAIN_ME,
    category: CATEGORY.GOV,
    severity: SEVERITY.ACTION,
    title: 'Train me: ContractPA needs 3 labeled examples for clause extraction',
    description: 'Unclaimed training request. Any reviewer on the team can pick it up.',
    refLabel: 'Train Me',
    actionLabel: 'Train',
    studio: 'governance-operations',
    studioExtraCount: 0,
    status: 'open',
    dueLabel: 'No date',
    dueBucket: DUE_BUCKET.NO_DATE,
    overdue: false,
    isCustomer: false,
    missionCritical: false,
    blocks: { workflows: 0, agents: 1 },
    commentsCount: 0,
    owner: null,
    situation: 'Model confidence dropped below 0.7 on renewal clauses. Needs labeled examples to recover.',
    detail: {
      submitter: { name: 'Morgan C.', role: 'Operations Manager', initials: 'MC' },
      submittedAt: 'Jun 11, 2026',
      note: 'Confidence dropped on renewal clauses after the Q2 contract pack landed.',
      fieldName: 'Clause extraction examples',
      currentValue: '1 labeled example',
      proposedValue: '4 labeled examples',
      sourceRecord: 'CONTRACTPA-RENEW-018',
      partition: 'contracts/renewal',
      version: 'v3',
      impact: {
        agents: ['ContractPA'],
        workflows: ['Renewal Clause Extraction'],
      },
    },
    pipeline: ['Collect', 'Label', 'Retrain'],
    destination: 'Sandbox Plane',
  },
  {
    id: 'EVT-209',
    scope: SCOPE.MY_TEAM,
    type: EVENT_TYPE.CLIENT_CONTINUATION,
    category: CATEGORY.CLIENT,
    severity: SEVERITY.CRITICAL,
    title: 'Escalated to me: Globex disputes the Q2 usage reconciliation',
    description: 'Reassigned by Quinn S. after the client escalated. Response committed for today.',
    refId: 'ACC-3120',
    refLabel: 'Client Continuation',
    actionLabel: 'Respond',
    studio: 'governance-operations',
    studioExtraCount: 0,
    status: 'open',
    dueLabel: 'Due today',
    dueBucket: DUE_BUCKET.DUE_TODAY,
    overdue: false,
    isCustomer: true,
    missionCritical: true,
    blocks: { workflows: 2, agents: 0 },
    commentsCount: 5,
    owner: { id: 'marcus-h', name: 'Marcus H.', initials: 'MH', isMine: true },
    situation: 'Client disputes 12% of the reconciled usage. Evidence pack is ready for review.',
    pipeline: ['Reconcile', 'Respond', 'Close'],
    destination: 'Client Plane',
    detail: {
      workflow: 'Q2 Usage Reconciliation',
      agent: 'BillingPA',
      model: 'GE-Comms-v2.1',
      confidence: 0.62,
      geClassification: 'GE-FIN',
      ucpProfileUrl: 'UCP-ACC-3120',
      traceRef: 'WF-6634',
      output: {
        channel: 'Email',
        recipient: 'ap@globex.com',
        subject: 'Re: Q2 usage reconciliation',
        draft:
          'Hi — we reviewed the 12% disputed lines against the evidence pack. Six of eight hold; two need a credit. Draft reply is ready to send once you approve.',
      },
      trace: [
        {
          label: 'Dispute Ingested',
          description: '12% of the reconciled Q2 usage marked as disputed',
          timestamp: '08:05 AM',
          state: 'done',
        },
        {
          label: 'Evidence Pack Assembled',
          description: 'Eight disputed lines matched against metering records',
          timestamp: '08:31 AM',
          state: 'done',
        },
        {
          label: 'Confidence Check',
          description: 'Confidence 0.62 — GE-FIN requires human sign-off',
          timestamp: '08:32 AM',
          state: 'done',
        },
        {
          label: 'HITL Gate',
          description: 'Waiting for approval before replying to the client',
          timestamp: '08:32 AM',
          state: 'paused',
        },
        {
          label: 'Send / Cancel',
          description: 'Action pending human decision',
          state: 'pending',
        },
      ],
    },
  },
  {
    id: 'EVT-210',
    scope: SCOPE.MY_TEAM,
    type: EVENT_TYPE.HTL_CONTINUATION,
    category: CATEGORY.AGNT,
    severity: SEVERITY.ACTION,
    title: 'HITL Pause: OnboardingPA waiting on a sandbox approval',
    description: 'Luis F. owns the agentic queue for this workflow.',
    refLabel: 'HTL Continuation',
    actionLabel: 'Approve',
    studio: 'agentic-oversight',
    studioExtraCount: 0,
    status: 'paused',
    dueLabel: 'Jun 26',
    dueBucket: DUE_BUCKET.THIS_WEEK,
    overdue: false,
    isCustomer: false,
    missionCritical: false,
    blocks: { workflows: 1, agents: 1 },
    commentsCount: 0,
    owner: { id: 'luis-f', name: 'Luis F.', initials: 'LF', isMine: false },
    situation: 'Agent paused before provisioning a sandbox tenant for a new client.',
    pipeline: ['Intent', 'Provision', 'Verify'],
    destination: 'Sandbox Plane',
    detail: {
      workflow: 'Client Sandbox Onboarding',
      agent: 'OnboardingPA',
      model: 'GE-Ops-v1.4',
      confidence: 0.81,
      geClassification: 'GE-OPS',
      traceRef: 'WF-9051',
      output: {
        channel: 'Internal ticket',
        recipient: 'sandbox-ops@aimsos.ai',
        subject: 'Provision sandbox tenant — pending HTL',
        draft:
          'Ready to provision a sandbox tenant for the new client workspace. Policy checks passed. Approve to continue onboarding.',
      },
      trace: [
        {
          label: 'Intent Classified',
          description: 'Sandbox tenant requested for a new client workspace',
          timestamp: '02:44 PM',
          state: 'done',
        },
        {
          label: 'Policy Check',
          description: 'All provisioning policies passed — confidence 0.81',
          timestamp: '02:45 PM',
          state: 'done',
        },
        {
          label: 'HITL Gate',
          description: 'Waiting for approval to provision the tenant',
          timestamp: '02:45 PM',
          state: 'paused',
        },
        {
          label: 'Provision / Cancel',
          description: 'Action pending human decision',
          state: 'pending',
        },
      ],
    },
  },
];

/**
 * Comment threads keyed by event id. Shared by the Thread tab (CT-4856).
 * Only EVT-001 carries the full prototype thread; others start empty.
 */
export const MOCK_THREADS = {
  'EVT-001': {
    status: 'open',
    comments: [
      {
        id: 'c1',
        author: 'Priya S.',
        role: 'Compliance Reviewer',
        initials: 'PS',
        timeAgo: '2h ago',
        body: 'Can someone confirm which source takes precedence for the GE-COMP threshold conflict? @Marcus A.',
      },
      {
        id: 'c2',
        author: 'Marcus A.',
        role: 'Governance Analyst',
        initials: 'MA',
        timeAgo: '1h ago',
        body: 'Source B is the one aligned with the latest compliance update. Approve against that.',
      },
    ],
  },
  'EVT-201': {
    status: 'open',
    comments: [
      {
        id: 'c1',
        author: 'Jordan T.',
        role: 'Compliance Reviewer',
        initials: 'JT',
        timeAgo: '45m ago',
        body: 'Working the pricing conflict now. Will flag here if I need a second approver.',
      },
    ],
  },
  'EVT-004': {
    status: 'open',
    comments: [
      {
        id: 'c1',
        author: 'Devon N.',
        role: 'IT Security',
        initials: 'DN',
        timeAgo: '15d ago',
        body: 'First approval logged. Standing by for your second sign-off — this is time-boxed to 4 hours per policy.',
      },
      {
        id: 'c2',
        author: 'Alexa M.',
        role: 'Admin',
        initials: 'AM',
        timeAgo: '15d ago',
        body: 'Reviewing the incident ticket now, will approve within the hour.',
      },
    ],
  },
  'EVT-205': {
    status: 'open',
    comments: [
      {
        id: 'c1',
        author: 'Sam R.',
        role: 'Data Engineer',
        initials: 'SR',
        timeAgo: '3h ago',
        body: 'PaymentsPA pulled the emergency credential at 02:14 — outside CHG window. Run logs are on IR-2026-0620.',
      },
      {
        id: 'c2',
        author: 'Quinn S.',
        role: 'Executive',
        initials: 'QS',
        timeAgo: '1h ago',
        body: 'Still unowned. Need two-key Break Glass before we attest or revoke the session — someone take it today.',
      },
    ],
  },
  'EVT-022': {
    status: 'open',
    comments: [
      {
        id: 'c-broadcast',
        author: 'Quinn S.',
        role: 'CTO',
        initials: 'QS',
        timeAgo: '1h ago',
        body: "Platform-wide: Governance is in Watch. Let's close the KCON and promotion-rate dip this week.",
        kind: 'message',
      },
    ],
  },
};

/**
 * Attestation panel rows keyed by event id (aims-htl `.wqep-attest-*`).
 * Status: `pending` | `verified` | `rejected`.
 */
export const MOCK_ATTESTATIONS = {
  'EVT-004': [
    {
      id: 'att1',
      name: 'Marcus H.',
      timestamp: 'Jun 17, 05:30 AM',
      status: 'pending',
    },
  ],
  'EVT-205': [
    {
      id: 'att1',
      name: 'PaymentsPA',
      timestamp: 'Jun 20, 02:20 AM',
      status: 'pending',
    },
  ],
};

/** Audit-trail entries keyed by event id. Backed by CT-4867's audit-thread endpoint later. */
export const MOCK_AUDIT_TRAIL = {
  'EVT-001': [
    { id: 'a1', actor: 'System', action: 'Event created from DIAN intake', timeAgo: '3h ago' },
    { id: 'a2', actor: 'FinancePolicyBot', action: 'Paused — 2 conflicts detected', timeAgo: '3h ago' },
    { id: 'a3', actor: 'Priya S.', action: 'Opened comment thread', timeAgo: '2h ago' },
  ],
  'EVT-004': [
    {
      id: 'a1',
      actor: 'Devon N.',
      action: 'Approved Break Glass',
      timestamp: 'Jun 17, 03:10 AM',
    },
  ],
  'EVT-201': [
    { id: 'a1', actor: 'System', action: 'Event created from KCON intake', timeAgo: '5h ago' },
    { id: 'a2', actor: 'Marcus H.', action: 'Assigned to Jordan T.', timeAgo: '4h ago' },
  ],
  'EVT-205': [
    {
      id: 'a1',
      actor: 'System',
      action: 'Break-glass credential use detected',
      timestamp: 'Jun 20, 02:14 AM',
    },
    {
      id: 'a2',
      actor: 'Quinn S.',
      action: 'Flagged as unowned — overdue',
      timestamp: 'Jun 20, 05:10 AM',
    },
  ],
  'EVT-209': [
    { id: 'a1', actor: 'System', action: 'Event created from client escalation', timeAgo: '1d ago' },
    { id: 'a2', actor: 'Quinn S.', action: 'Reassigned to Marcus H.', timeAgo: '5h ago' },
  ],
};
